import humanize from "underscore.string/humanize";
import moment from "moment-timezone";

import { formatCurrency } from "@eave/shared/utils/currency";

import { ColumnDef, ColumnDisplay, ColumnKind, Schema } from "./shared";
import { DisplayValue } from "./operators";

export const orderFunc = (nullsFirst: boolean) => (a: any, b: any) => {
  if (a === b) {
    return 0;
  }
  if (Number.isNaN(a) && Number.isNaN(b)) {
    return 0;
  }

  if (a === undefined || a === null || a === "" || Number.isNaN(a)) {
    if (nullsFirst) {
      return -1;
    }
    return 1;
  }
  if (b === undefined || b === null || b === "" || Number.isNaN(b)) {
    if (nullsFirst) {
      return 1;
    }
    return -1;
  }

  if (a < b) {
    return -1;
  }
  if (a > b) {
    return 1;
  }

  return 0;
};

export const moneyOrderFunc = (nullsFirst: boolean) => {
  const func = orderFunc(nullsFirst);
  return (a, b) => func(parseFloat(a), parseFloat(b));
};

export const orderFuncFromArray = (keys: string[], nullsFirst: boolean) => {
  const sort = keys.reduce(
    (accumulator, currentValue, i) => ({ ...accumulator, [currentValue]: i }),
    {},
  );
  const func = orderFunc(nullsFirst);
  return (a, b) => func(sort[a], sort[b]);
};

export interface EnumOption {
  value: string;
  label: string;
}

export const defBoolean = (
  jsonKey: string,
  options?: { displayName?: string; orderFunc?: (a: any, b: any) => number },
): ColumnDef => ({
  jsonKey,
  kind: ColumnKind.Boolean,
  displayName: (options && options.displayName) || humanize(jsonKey),
  orderFunc: (options && options.orderFunc) || orderFunc(false),
});

export const defDate = (
  jsonKey: string,
  options?: { displayName?: string; orderFunc?: (a: any, b: any) => number },
): ColumnDef => ({
  jsonKey,
  kind: ColumnKind.Date,
  displayName: (options && options.displayName) || humanize(jsonKey),
  orderFunc: (options && options.orderFunc) || orderFunc(false),
});

export const defEnum = (
  jsonKey: string,
  enums: EnumOption[],
  options?: { displayName?: string; orderFunc?: (a: any, b: any) => number },
): ColumnDef => ({
  jsonKey,
  kind: ColumnKind.Enum,
  displayName: (options && options.displayName) || humanize(jsonKey),
  orderFunc: (options && options.orderFunc) || orderFunc(false),
  enums: [
    { value: null, label: DisplayValue.Empty }, // so we can multi-select, e.g., "A + B + <empty>"
    ...enums,
  ],
});

export const defMoney = (
  jsonKey: string,
  options?: { displayName?: string; orderFunc?: (a: any, b: any) => number },
): ColumnDef => ({
  jsonKey,
  kind: ColumnKind.Money,
  displayName: (options && options.displayName) || humanize(jsonKey),
  orderFunc: (options && options.orderFunc) || moneyOrderFunc(false),
});

export const defNumber = (
  jsonKey: string,
  options?: { displayName?: string; orderFunc?: (a: any, b: any) => number },
): ColumnDef => ({
  jsonKey,
  kind: ColumnKind.Number,
  displayName: (options && options.displayName) || humanize(jsonKey),
  orderFunc: (options && options.orderFunc) || orderFunc(false),
});

export const defText = (
  jsonKey: string,
  options?: { displayName?: string; orderFunc?: (a: any, b: any) => number },
): ColumnDef => ({
  jsonKey,
  kind: ColumnKind.Text,
  displayName: (options && options.displayName) || humanize(jsonKey),
  orderFunc: (options && options.orderFunc) || orderFunc(false),
});

export const defTime = (
  jsonKey: string,
  options?: { displayName?: string; orderFunc?: (a: any, b: any) => number },
): ColumnDef => ({
  jsonKey,
  kind: ColumnKind.Time,
  displayName: (options && options.displayName) || humanize(jsonKey),
  orderFunc: (options && options.orderFunc) || orderFunc(false),
});

export const getColumnDefByJsonKey = (s: Schema, jsonKey: string): ColumnDef => {
  const def = s.find((d: ColumnDef) => d.jsonKey === jsonKey);
  if (!def) {
    throw new Error(`could not find column with jsonKey "${jsonKey}" in schema`);
  }
  return def;
};

export const getColumnDefFromDisplayName = (s: Schema, displayName: string): ColumnDef => {
  const def = s.find((d: ColumnDef) => d.displayName === displayName);
  if (!def) {
    throw new Error(`could not find column with display name "${displayName}" in schema`);
  }
  return def;
};

export const columnIsVisible = (cd: ColumnDisplay[], jsonKey: string): boolean => {
  const display = cd.find((d: ColumnDisplay) => d.jsonKey === jsonKey);
  if (!display) {
    throw new Error(`could not find column key "${jsonKey}" in column display`);
  }
  return display.visible;
};

// Formats a value based on its ColumnKind
export const formattedValue = (kind: ColumnKind, value: any, enums?) => {
  switch (kind) {
    case ColumnKind.Number:
      return `${value}`;
    case ColumnKind.Money:
      return formatCurrency(value, 2, 2);
    case ColumnKind.Boolean:
      return value ? "Yes" : "No"; // Assumes header text phrasing will match
    case ColumnKind.Date:
      return moment(value).tz(moment.tz.guess()).format("MM/DD/YYYY");
    case ColumnKind.Time:
      return moment(value).tz(moment.tz.guess()).format("M/D/YY H:mm z");
    case ColumnKind.Enum: {
      const match = enums.find(e => value === e.value);
      if (!match) {
        return humanize(value);
      }
      return match.label;
    }
    default:
      return value;
  }
};

export const enumValueToEnumOption = (enums: EnumOption[], value: string): EnumOption => {
  const e = enums.find(e => e.value === value);
  if (e) {
    return e;
  }

  // if we encounter a historical value that's no longer present in the enum definition, we need to
  // synthesize a display value for it - note that this is for display purposes only, and that enum
  // option will no longer be available once edits occur, so there's no data cleanup we need to do
  return {
    value,
    label: value,
  };
};
